import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, ShieldCheck, Rows3, Rows4 } from 'lucide-react';

function roleLabel(user) {
  if (!user) return '';
  if (user.role === 'SuperAdmin') return 'Super Admin · root';
  if (user.role === 'AllIndiaManager') return 'All-India Manager';
  if (user.role === 'RetailManager') return 'Retail Manager';
  if (user.role === 'ZoneManager') return `Zone Manager · ${user.zone || '—'}`;
  return `${user.role}${user.branchCode ? ` · ${user.branchCode}` : ''}`;
}

export default function TopHeader() {
  const { user, signOut, theme, density, toggleDensity } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const isCompact = density === 'compact';

  return (
    <header
      className="sticky top-0 z-20 flex items-center justify-between gap-3 px-6 md:px-8 h-14 border-b border-[#E7E2D3]"
      style={{ background: 'rgba(245,241,231,0.92)', backdropFilter: 'blur(6px)' }}
      data-testid="app-top-header"
    >
      <div className="flex items-center gap-3">
        <div
          className="hidden md:flex items-center gap-2 text-[10.5px] tracking-[0.18em] uppercase text-[#7a6f5c]"
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: theme.primary }} />
          <span>CORE FUTURE</span>
          <span className="opacity-40">·</span>
          <span className="normal-case tracking-normal text-[11px]">post.futuremakeup.com</span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-right hidden sm:block">
          <div className="text-[13px] font-semibold text-[#232323] flex items-center justify-end gap-1.5" data-testid="header-user-name">
            {user.role === 'SuperAdmin' && <ShieldCheck size={12} style={{ color: theme.primary }} />}
            {user.name}
          </div>
          <div className="text-[10.5px] text-[#7a6f5c]">{roleLabel(user)}</div>
        </div>
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[12px] font-semibold"
          style={{ background: theme.primary }}
          aria-hidden="true"
        >
          {(user.name || '?').slice(0, 1).toUpperCase()}
        </div>
        <button
          onClick={toggleDensity}
          data-testid="header-density-toggle"
          aria-label={isCompact ? 'Switch to comfortable rows' : 'Switch to compact rows'}
          title={isCompact ? 'Comfortable rows' : 'Compact rows — see ~2× more per screen'}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[#E7E2D3] bg-white text-[12.5px] text-[#232323] hover:bg-[#F7F4EA] transition-colors"
        >
          {isCompact ? <Rows4 size={13} /> : <Rows3 size={13} />}
          <span className="hidden md:inline">{isCompact ? 'Compact' : 'Cozy'}</span>
        </button>
        <button
          onClick={() => { signOut(); navigate('/login'); }}
          data-testid="header-signout-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E7E2D3] bg-white text-[12.5px] text-[#232323] hover:bg-[#F7F4EA] transition-colors"
        >
          <LogOut size={13} /> Sign out
        </button>
      </div>
    </header>
  );
}
